import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AuthLayout, Button, Field, Input, PasswordInput } from '@lp/portal-ui';
import { acceptInviteSchema, type StaffSelf } from '@lp/shared';
import { api } from '../api.ts';
import { errorCode, errorFields, errorMessage } from '../lib/errors.ts';
import { type FieldErrors, validate } from '../lib/validate.ts';
import { Notice } from '../components/ui.tsx';

const PASSWORD_RULE = '最少 10 個字元，須包含英文字母及數字';

export default function InvitePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (token.length < 20) {
    return (
      <AuthLayout subtitle="品牌後台" title="接受邀請">
        <Notice tone="warning" title="邀請連結不完整">
          請直接從邀請電郵開啟連結；如連結已失效，請聯絡品牌負責人重新發出邀請。
        </Notice>
        <Link to="/login" className="mt-6 block text-center text-sm font-medium text-brand-700 hover:underline">
          前往登入
        </Link>
      </AuthLayout>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const v = validate(acceptInviteSchema, { token, name, password }, { password: PASSWORD_RULE, name: '請輸入你的名稱' });
    const errs: FieldErrors = v.ok ? {} : { ...v.errors };
    if (password && confirm !== password) errs.confirm = '兩次輸入的密碼不相同';
    if (!v.ok || Object.keys(errs).length) {
      setErrors(errs);
      if (errs.token) setFormError('邀請連結無效，請聯絡品牌負責人重新發出邀請。');
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await api.post<{ user: StaffSelf }>('/auth/invite/accept', v.data);
      navigate('/', { replace: true });
    } catch (err) {
      if (errorCode(err) === 'VALIDATION_FAILED') setErrors(errorFields(err));
      setFormError(
        errorMessage(err, {
          NOT_FOUND: '邀請連結無效或已被使用。',
          CODE_INVALID: '邀請連結無效或已被使用。',
          CODE_EXPIRED: '邀請連結已過期，請聯絡品牌負責人重新發出邀請。',
        }),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout subtitle="品牌後台" title="設定你的帳戶">
      <p className="-mt-3 mb-6 text-sm text-muted">你獲邀加入品牌後台。請設定顯示名稱及登入密碼。</p>
      <form onSubmit={submit} className="space-y-5" noValidate>
        {formError && <Notice tone="danger">{formError}</Notice>}
        <Field label="你的名稱" error={errors.name} required>
          {(id) => <Input id={id} autoComplete="name" autoFocus value={name} invalid={!!errors.name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="密碼" error={errors.password} hint={PASSWORD_RULE} required>
          {(id) => (
            <PasswordInput id={id} autoComplete="new-password" value={password} invalid={!!errors.password} onChange={(e) => setPassword(e.target.value)} />
          )}
        </Field>
        <Field label="確認密碼" error={errors.confirm} required>
          {(id) => (
            <PasswordInput id={id} autoComplete="new-password" value={confirm} invalid={!!errors.confirm} onChange={(e) => setConfirm(e.target.value)} />
          )}
        </Field>
        <Button type="submit" loading={busy} className="w-full">
          建立帳戶並登入
        </Button>
      </form>
    </AuthLayout>
  );
}
